import creditsData from './credits.json'
import postersData from './posters.json'
import profileData from './profile.json'

export interface Credit {
  slug: string
  title: string
  broadcaster: string
  dates: string
  role: string
}

export interface PosterRecord {
  slug: string
  title: string
  status: 'found' | 'missing'
}

export interface CollageImage {
  kind: 'image'
  slug: string
  title: string
  src: string
  alt: string
}

export interface CollagePlaceholder {
  kind: 'placeholder'
  slug: string
  title: string
  kicker: string
  broadcaster: string
}

export type CollageTile = CollageImage | CollagePlaceholder

export interface TimelineImage {
  src: string
  alt: string
}

export interface TimelineItem {
  slug: string
  year: string
  title: string
  role: string
  broadcaster: string
  dates: string
  meta: string
  image: TimelineImage | null
}

export interface HomepageContent {
  hero: {
    kicker: string
    role: string
    medium: string
  }
  collage: CollageTile[]
  credits: TimelineItem[]
  skills: string[]
  education: {
    qualification: string
    institution: string
    years: string
  }
  profile: string
}

/**
 * Copy is taken from the approved design 3 mock-up (design-c3.html,
 * credits-c3.json, posters-c3.json, profiles-c2.json option v2).
 * The hero role was set back to Junior Assistant Set Decorator after review.
 * Individual credit roles are unchanged.
 */
export const HERO = {
  kicker: 'Set Decoration',
  role: 'Junior Assistant Set Decorator',
  medium: 'Television & Film',
} as const

export const SKILLS = [
  'Illustrator & Photoshop',
  'Digital P.O’s',
  'Budgeting',
  'Dressing Plans & SketchUp',
] as const

export const EDUCATION = {
  qualification: 'First Class Bachelor of Arts in Design for Theatre and Film',
  institution: 'University for the Creative Arts',
  years: '2016 – 2019',
} as const

export const COMING_SOON_LABEL = 'Coming soon'

const credits = creditsData as Credit[]
const posters = postersData as PosterRecord[]

const postersBySlug = new Map(posters.map(poster => [poster.slug, poster]))

/**
 * Year chip from the design generator: a single year, or a range with the
 * end year shortened (2025–26).
 */
export function creditYearLabel(dates: string): string {
  const years = dates.match(/20\d\d/g) ?? []
  if (years.length === 0) {
    return dates
  }

  const first = years[0] ?? dates
  const last = years[years.length - 1] ?? first
  if (years.length === 1 || first === last) {
    return first
  }

  return `${first}–${last.slice(2)}`
}

/**
 * Collage order follows the credits list, with Prime Target and His Dark
 * Materials swapped so Prime Target sits in the edge column and Silo stays
 * nearer the centre. Matches design-c3.html.
 */
export function collageCredits<T extends { slug: string }>(items: readonly T[]): T[] {
  const tiles = [...items]
  const prime = tiles.findIndex(item => item.slug === 'prime-target')
  const materials = tiles.findIndex(item => item.slug === 'his-dark-materials')
  if (prime === -1 || materials === -1) {
    return tiles
  }

  const primeTile = tiles[prime]
  const materialsTile = tiles[materials]
  if (primeTile === undefined || materialsTile === undefined) {
    return tiles
  }

  tiles[prime] = materialsTile
  tiles[materials] = primeTile
  return tiles
}

function posterSrc(slug: string): string {
  return `/posters/${slug}.webp`
}

function hasPoster(slug: string): boolean {
  return postersBySlug.get(slug)?.status === 'found'
}

export function creditMeta(credit: Credit): string {
  return `${credit.role} · ${credit.broadcaster} · ${credit.dates}`
}

export function buildHomepage(): HomepageContent {
  return {
    hero: HERO,
    collage: collageCredits(credits).map((credit) => {
      if (!hasPoster(credit.slug)) {
        return {
          kind: 'placeholder',
          slug: credit.slug,
          title: credit.title,
          kicker: COMING_SOON_LABEL,
          broadcaster: credit.broadcaster,
        }
      }

      return {
        kind: 'image',
        slug: credit.slug,
        title: credit.title,
        src: posterSrc(credit.slug),
        alt: `${credit.title} poster`,
      }
    }),
    credits: credits.map((credit) => ({
      slug: credit.slug,
      year: creditYearLabel(credit.dates),
      title: credit.title,
      role: credit.role,
      broadcaster: credit.broadcaster,
      dates: credit.dates,
      meta: creditMeta(credit),
      image: hasPoster(credit.slug)
        ? { src: posterSrc(credit.slug), alt: '' }
        : null,
    })),
    skills: [...SKILLS],
    education: EDUCATION,
    profile: profileData.text,
  }
}
